import * as React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { keyframes } from "@emotion/react";

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.18); }
`;

type Props = {
  displayName: string;
  profileCompletionPercent: number;
  newCardCount?: number;
  heartBalance?: number;
  unreadMessageCount?: number;
};

export default function HeroBanner({
  displayName,
  profileCompletionPercent,
  newCardCount,
  heartBalance,
  unreadMessageCount,
}: Props) {
  const navigate = useNavigate();
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good Morning" : hour < 18 ? "Good Afternoon" : "Good Evening";

  const cardCount = newCardCount ?? 0;
  const hearts = heartBalance ?? 0;
  const messages = unreadMessageCount ?? 0;
  const isHeartLow = hearts <= 3;

  const handleCardClick = () => {
    const el = document.getElementById("cupid-cards-section");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const stats = [
    {
      value: cardCount,
      label: `새 카드 ${cardCount}장 ›`,
      onClick: handleCardClick,
      bg: "rgba(255,255,255,0.07)",
      border: "rgba(255,255,255,0.1)",
      hoverBg: "rgba(255,255,255,0.18)",
      isHeart: false,
      isMessage: false,
    },
    {
      value: hearts,
      label: isHeartLow ? "하트 부족 · 충전 ›" : `하트 ${hearts}개 ›`,
      onClick: () => navigate("/wallet"),
      bg: "rgba(255, 90, 90, 0.2)",
      border: isHeartLow ? "rgba(255, 60, 60, 0.6)" : "rgba(255,120,120,0.4)",
      hoverBg: "rgba(255, 90, 90, 0.35)",
      isHeart: true,
      isMessage: false,
    },
    {
      value: messages,
      label: messages > 0 ? `메시지 ${messages}건 ›` : "메시지함 ›",
      onClick: () => navigate("/messages"),
      bg: "rgba(255,255,255,0.07)",
      border: "rgba(255,255,255,0.1)",
      hoverBg: "rgba(255,255,255,0.18)",
      isHeart: false,
      isMessage: true,
    },
  ];

  return (
    <Box
      sx={{
        background: "linear-gradient(160deg, #0D1F3C 0%, #1A3360 55%, #283C6E 100%)",
        borderRadius: { xs: "0 0 24px 24px", md: "20px" },
        p: { xs: "32px 24px 34px", md: "36px 40px" },
        position: "relative",
        overflow: "hidden",
        mb: { xs: 0, md: "28px" },
      }}
    >
      {/* 우측 상단 장식 원 (대) */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: -30, md: -40 },
          right: { xs: -30, md: -40 },
          width: { xs: 160, md: 220 },
          height: { xs: 160, md: 220 },
          borderRadius: "50%",
          border: "1.5px solid rgba(201,168,108,0.2)",
          pointerEvents: "none",
        }}
      />
      {/* 우측 상단 장식 원 (소) */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: -10, md: -10 },
          right: { xs: -10, md: -10 },
          width: { xs: 100, md: 130 },
          height: { xs: 100, md: 130 },
          borderRadius: "50%",
          border: "1px solid rgba(201,168,108,0.12)",
          pointerEvents: "none",
        }}
      />

      {/* 콘텐츠 */}
      <Box
        sx={{
          position: "relative",
          display: { xs: "block", md: "flex" },
          alignItems: { md: "flex-end" },
          gap: { md: "40px" },
        }}
      >
        {/* 텍스트 영역 */}
        <Box sx={{ flex: 1 }}>
          {/* Eyebrow */}
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "2px",
              textTransform: "uppercase",
              color: "#C9A86C",
              mb: "12px",
              opacity: 0.9,
            }}
          >
            {greeting}
          </Typography>

          {/* 메인 헤딩 */}
          <Typography
            component="h1"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontSize: { xs: "30px", md: "36px" },
              fontWeight: 700,
              color: "#FFFFFF",
              lineHeight: 1.2,
              mb: "14px",
              wordBreak: "keep-all",
              overflowWrap: "break-word",
            }}
          >
            {displayName} 님,
            <br />
            <Box component="em" sx={{ fontStyle: "italic", color: "#E8CFA0" }}>
              오늘의 인연
            </Box>
            이{" "}
            <Box component="span" sx={{ display: { xs: "inline", md: "inline" } }}>
              기다립니다
            </Box>
          </Typography>

          {/* 서브 메시지 */}
          <Typography
            sx={{
              fontSize: { xs: "14px", md: "15px" },
              color: "rgba(255,255,255,0.65)",
              lineHeight: 1.7,
              mb: { xs: "20px", md: 0 },
            }}
          >
            {cardCount > 0 ? (
              <>
                품격 있는 만남을 위해 엄선된
                <br />
                큐피트 카드가 도착했습니다.
              </>
            ) : (
              <>
                새로운 인연을 위해
                <br />
                큐피트 카드를 준비하고 있습니다.
              </>
            )}
          </Typography>

          {/* 프로그레스 바 (모바일만) */}
          {profileCompletionPercent < 100 && (
            <Box
              sx={{
                display: { xs: "flex", md: "none" },
                alignItems: "center",
                gap: "12px",
                mt: "20px",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  height: "3px",
                  bgcolor: "rgba(255,255,255,0.15)",
                  borderRadius: "2px",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${profileCompletionPercent}%`,
                    background: "linear-gradient(90deg, #C9A86C, #E8CFA0)",
                    borderRadius: "2px",
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "rgba(255,255,255,0.5)",
                  whiteSpace: "nowrap",
                }}
              >
                프로필 {profileCompletionPercent}%
              </Typography>
            </Box>
          )}
        </Box>

        {/* 통계 카드 (데스크톱만) */}
        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            gap: "24px",
            flexShrink: 0,
          }}
        >
          {stats.map((stat, i) => (
            <Box
              key={i}
              onClick={stat.onClick}
              sx={{
                textAlign: "center",
                bgcolor: stat.bg,
                border: `1px solid ${stat.border}`,
                borderRadius: "14px",
                p: "16px 24px",
                minWidth: "90px",
                minHeight: "72px",
                cursor: "pointer",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.15s ease",
                "&:hover": {
                  bgcolor: stat.hoverBg,
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                  borderColor: "rgba(255,255,255,0.3)",
                },
                "&:active": {
                  transform: "translateY(1px)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                },
              }}
            >
              <Box sx={{ position: "relative", display: "inline-block" }}>
                <Typography
                  sx={{
                    fontFamily: '"Playfair Display", serif',
                    fontSize: "28px",
                    fontWeight: 700,
                    color: "#C9A86C",
                    lineHeight: 1,
                    display: "inline-block",
                    ...(stat.isHeart && {
                      animation: `${pulse} 2s ease-in-out infinite`,
                    }),
                  }}
                >
                  {stat.value}
                </Typography>
                {stat.isMessage && messages > 0 && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -4,
                      right: -10,
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      bgcolor: "#FF4444",
                      border: "1.5px solid rgba(13,31,60,0.8)",
                    }}
                  />
                )}
              </Box>
              <Typography
                sx={{
                  fontSize: "11px",
                  color:
                    isHeartLow && stat.isHeart
                      ? "rgba(255,120,120,0.9)"
                      : "rgba(255,255,255,0.55)",
                  mt: "6px",
                  letterSpacing: "0.3px",
                  whiteSpace: "nowrap",
                }}
              >
                {stat.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
